import { cloneDeep, isUndefined } from 'lodash';
import { v4 as uuid } from 'uuid';
import { exportLayerOriginInfo, resizeLayers, scaleLayer, scaleLayers, trackConfig } from '@/framework';
import { Framework } from '@/framework/instance';
import { IReplaceLayerResult } from '@/framework/stage/states/stage-state';
import { getBackgroundLayerProps, isFitHeight } from '@/utils/layer-kit';
import { SizeUnitType } from '@/utils/unit-kit';
import {
    CanvasType,
    DirectionType,
    IExtraInfo,
    ILayerProps,
    IPoint,
    ISize,
    IStageResizeData,
    LayerType,
    StageType,
} from '@/version-types';
import {
    historyAdjustType,
    historyBaseType,
    historyReplaceLayerType,
    IAdjustUnit,
    IStageHistoryBaseData,
} from './stage-history';

export class Stage extends Framework {
    /** 创建舞台字体子集 insert, clone, replace */
    private async createStagesFontFamilySubset(originIndexs: number[]) {
        // const layers: ILayerProps[] = [];
        // for (let i = 0; i < originIndexs.length; i++) {
        //     layers.push(...this.framework.getLayers(originIndexs[i]));
        // }
        // await this.framework.createFontFamilySubset(layers, true);
        for (let i = 0; i < originIndexs.length; i++) {
            await this.framework.createFontFamilySubset(this.framework.getLayers(originIndexs[i]), true);
            this.framework.events.emit('fontFamilySubsetLoaded', this.framework.convertOriginToStage(originIndexs[i]));
        }
    }

    onFontFamilySubsetLoaded(fn: (originIndex: number) => void) {
        return this.framework.events.on('fontFamilySubsetLoaded', fn);
    }

    /** 插入舞台 */
    private async insertStage(
        layers: ILayerProps[],
        extraInfo: IExtraInfo,
        scaleWidth: boolean,
        index?: number,
        trackFunc?: string,
    ) {
        this.framework.operate.delActiveLayer();
        const { originIndexs, insertId } = await this.framework.history.realEdit.insertStage(1);
        const insertData = insertId
            ? cloneDeep({
                  insertId,
                  originIndexs,
                  extraInfos: [extraInfo],
                  stages: scaleWidth ? [] : [layers],
              })
            : undefined;
        this.framework.addExtraInfos([extraInfo], originIndexs);
        const stageType = this.framework.stage.getStageType();
        let stageScaleWidth = 0;
        if (stageType === StageType.VARIETY) {
            stageScaleWidth = this.framework.stage.getSize().width;
        }
        const { addList, oldIndex, oldIds, newIndex, newIds } = this.framework.stage.addStages(
            layers,
            true,
            index,
            stageScaleWidth || scaleWidth,
            originIndexs,
        );
        if (scaleWidth && insertData) {
            insertData.stages = [this.framework.getLayers(originIndexs[0])];
        }
        const data: IStageHistoryBaseData = {
            oldIds,
            oldIndex,
            newIds,
            newIndex,
            insertData,
        };
        this.framework.history.write({
            type: historyBaseType,
            data,
            trackFunc: trackFunc || trackConfig.page.replace,
            skipCheckOriginIndex: true,
        });
        // 创建字体子集
        await this.createStagesFontFamilySubset(addList);
    }

    /** 获取空白舞台 */
    private getEmptyStage(removeExtraInfo = true) {
        const originIndex = this.framework.stage.getActiveOriginIndex();
        const backgroundLayer = cloneDeep(this.framework.getBackgroundLayer(originIndex)) || ({} as ILayerProps);
        if (removeExtraInfo && backgroundLayer.extraInfo) {
            delete backgroundLayer.extraInfo.label;
        }
        const layers: ILayerProps[] = [
            getBackgroundLayerProps({
                ...backgroundLayer,
                materialIds: {},
                id: uuid(),
                opacity: 1,
                data: {
                    backgroundColor: '#FFFFFFFF',
                    backgroundGradient: null,
                    cutoutMaskURL: '',
                    id: 1,
                    imageTransform: {
                        flipH: false,
                        flipV: false,
                        heightRatio: 1,
                        offsetX: 0,
                        offsetY: 0,
                        rotate: 0,
                        scale: 1,
                        widthRatio: 1,
                    },
                    maskType: 0,
                    maskURL: '',
                    naturalHeight: 0,
                    naturalWidth: 0,
                    stretchOption: 2,
                    type: '',
                    url: '',
                },
            }),
        ];

        return layers;
    }

    /** 替换激活舞台 */
    async replaceActive(layers: ILayerProps[], extraInfo: IExtraInfo, scaleWidth: boolean, trackFunc?: string) {
        this.framework.operate.delActiveLayer();
        const { originIndexs, insertId } = await this.framework.history.realEdit.insertStage(1);
        const insertData = insertId
            ? cloneDeep({
                  insertId,
                  originIndexs,
                  extraInfos: [extraInfo],
                  stages: scaleWidth ? [] : [layers],
              })
            : undefined;
        this.framework.addExtraInfos([extraInfo], originIndexs);
        const { addList, oldIndex, oldIds, newIndex, newIds } = this.framework.stage.replaceActiveStage(
            layers,
            scaleWidth,
            originIndexs,
        );
        if (scaleWidth && insertData) {
            insertData.stages = [this.framework.getLayers(originIndexs[0])];
        }
        this.framework.history.write({
            type: historyBaseType,
            data: {
                insertId,
                oldIndex,
                oldIds,
                newIndex,
                newIds,
                _isReplaceStage: true,
                insertData,
                isReplaceActive: true,
            },
            trackFunc,
            skipCheckOriginIndex: true,
        });
        // 创建字体子集
        this.createStagesFontFamilySubset(addList);
    }

    /** 替换所有舞台 */
    async replaceAll(
        stages: ILayerProps[][],
        extraInfos: IExtraInfo[],
        trackFunc?: string,
        stageTypes?: { oldStageType: StageType; newStageType: StageType },
    ) {
        this.framework.operate.delActiveLayer();
        const { originIndexs, insertId } = await this.framework.history.realEdit.insertStage(extraInfos.length);
        const insertData = insertId
            ? cloneDeep({
                  insertId,
                  originIndexs,
                  extraInfos: extraInfos,
                  stages: stages,
              })
            : undefined;
        this.framework.addExtraInfos(extraInfos, originIndexs);
        if (stageTypes) {
            this.framework.stage.setStageType(stageTypes.newStageType, stageTypes.newStageType as any);
        }
        const { addList, oldIndex, oldIds, newIndex, newIds } = this.framework.stage.replaceAllStages(
            stages,
            originIndexs,
        );
        this.framework.history.write({
            type: historyBaseType,
            data: {
                insertId,
                oldIndex,
                oldIds,
                newIndex,
                newIds,
                stageTypes,
                _isReplaceStage: true,
                insertData,
            },
            trackFunc,
            skipCheckOriginIndex: true,
        });
        // 创建字体子集
        this.createStagesFontFamilySubset(addList);
    }

    /** 删除舞台 */
    delete(stageIndex: number) {
        const originIndex = this.framework.stage.getActiveOriginIndex();
        const { oldIds, oldIndex, newIds, newIndex } = this.framework.stage.delStage(stageIndex);
        this.framework.history.write({
            type: historyBaseType,
            data: {
                oldIds,
                oldIndex,
                newIds,
                newIndex,
                delData: [originIndex],
            },
            trackFunc: trackConfig.page.delete,
            originIndex,
            skipCheckOriginIndex: true,
        });
    }

    /** 批量删除舞台 */
    batchDelete(indices: number[]) {
        const delData = indices.map((item) => this.framework.convertStageToOrigin(item));
        const res = this.framework.stage.batchDelStage(indices);
        if (!res) return;
        const { oldIds, oldIndex, newIds, newIndex } = res;
        this.framework.history.write({
            type: historyBaseType,
            data: {
                oldIds,
                oldIndex,
                newIds,
                newIndex,
                delData,
            },
            trackFunc: trackConfig.page.delete,
            skipCheckOriginIndex: true,
        });

        return res;
    }

    /**
     * 添加空白画布，默认添加在最后
     *
     * @param stageIndex - 舞台索引序号
     * @param incomeExtraInfo - 额外信息
     * @param inheritBgInfo - 继承的背景信息
     */
    async create(
        stageIndex = -1,
        incomeExtraInfo = { threshold: 0 },
        inheritBgInfo?: ILayerProps,
        masterPage?: ILayerProps[],
    ) {
        const activeExtraInfo = this.framework.getExtraInfo(this.framework.stage.getActiveStageIndex());
        const extraInfo = {
            ...cloneDeep(activeExtraInfo),
            ...incomeExtraInfo,
        };
        const emptyStage = this.getEmptyStage();
        if (masterPage) {
            masterPage.find((item) => {
                if (item.layerType === LayerType.TEXT_LAYER && item.extraInfo?.label?.type === '内容标题') {
                    item.extraInfo.modified = false;
                }
            });
            emptyStage.push(...masterPage);
            emptyStage.splice(0, 1);
            if (!extraInfo.extraInfo) {
                extraInfo.extraInfo = {};
            }
            extraInfo.extraInfo.label = {
                theme: '浅色调',
                type: '内容',
            };
        } else if (inheritBgInfo) {
            if (extraInfo.extraInfo?.label) {
                delete extraInfo.extraInfo.label;
            }
            emptyStage[0] = inheritBgInfo;
        } else {
            if (extraInfo.extraInfo?.label) {
                delete extraInfo.extraInfo.label;
            }
        }
        const { originIndexs, insertId } = await this.framework.history.realEdit.insertStage(1);
        const insertData = insertId
            ? cloneDeep({
                  insertId,
                  originIndexs,
                  extraInfos: [extraInfo],
                  stages: [emptyStage],
              })
            : undefined;
        this.framework.addExtraInfos([extraInfo], originIndexs);
        const { oldIds, oldIndex, newIds, newIndex } = this.framework.stage.addStages(
            emptyStage,
            true,
            stageIndex === -1 ? undefined : stageIndex,
        );
        const data: IStageHistoryBaseData = {
            oldIds,
            oldIndex,
            newIds,
            newIndex,
            insertData,
        };
        this.framework.history.write({
            type: historyBaseType,
            data,
            trackFunc: trackConfig.page.add,
            skipCheckOriginIndex: true,
        });
    }

    /**
     * 克隆舞台
     */
    async clone(stageIndex: number, targetIndex?: number) {
        targetIndex = targetIndex ?? stageIndex;
        const layers = this.framework.stage.cloneStage(stageIndex);
        const extraInfo = cloneDeep(this.framework.getExtraInfo(stageIndex));
        const { originIndexs, insertId } = await this.framework.history.realEdit.insertStage(1);
        const insertData = insertId
            ? cloneDeep({
                  insertId,
                  originIndexs,
                  extraInfos: [extraInfo],
                  stages: [layers],
              })
            : undefined;
        this.framework.addExtraInfos([extraInfo], originIndexs);
        const { addList, oldIndex, oldIds, newIndex, newIds } = this.framework.stage.addStages(
            layers,
            true,
            targetIndex,
            undefined,
            originIndexs,
        );
        const data: IStageHistoryBaseData = {
            oldIds,
            oldIndex,
            newIds,
            newIndex,
            insertData,
        };
        this.framework.history.write({
            type: historyBaseType,
            data,
            trackFunc: trackConfig.page.copy,
            skipCheckOriginIndex: true,
        });
        // 创建字体子集
        this.createStagesFontFamilySubset(addList);
    }

    /**
     * 调整尺寸
     *
     * @param stageIndex - 舞台索引序号
     * @param currentStages - 当前数据
     * @param destStages - 目标数据
     * @param currentSize - 当前大小
     * @param destSize - 目标大小
     * @param units - 当前、目标单位
     * @param pushHistory - 可选；是否添加到历史记录中；默认值为 `true`
     */
    resize(
        stageIndex: number,
        currentStages: ILayerProps[][],
        destStages: ILayerProps[][],
        currentSize: ISize,
        destSize: ISize,
        units: IAdjustUnit,
        pushHistory = true,
        trackFunc?: string,
    ) {
        this.framework.operate.delActiveLayer();
        const originIndex = this.framework.convertStageToOrigin(stageIndex);
        for (let i = 0; i < destStages.length; i++) {
            const layers = destStages[i];
            resizeLayers(
                this.framework.identity,
                layers,
                currentSize,
                destSize,
                originIndex === this.framework.convertStageToOrigin(i),
            );
        }
        const newStages = cloneDeep(destStages);
        for (let i = 0; i < destStages.length; i++) {
            const layers = destStages[i];
            this.framework.stage.batchUpdate(layers, true, false);
        }
        this.framework.setRerender(originIndex);
        this.framework.rerender();
        // this.framework.updateExtraInfoSize(stageIndex);
        if (pushHistory) {
            this.framework.history.write({
                type: historyAdjustType,
                data: {
                    originIndex,
                    units,
                    oldStages: currentStages,
                    newStages,
                },
                trackFunc: trackFunc || trackConfig[LayerType.BACKGROUND_LAYER]?.resize,
            });
        }
    }

    /**
     * 缩放尺寸
     *
     * @param stageIndex - 舞台索引序号
     * @param currentStages - 当前数据
     * @param destStages - 目标数据
     * @param currentSize - 当前大小
     * @param destSize - 目标大小
     * @param units - 当前、目标单位
     * @param pushHistory - 可选；是否添加到历史记录中；默认值为 `true`
     * @param trackFunc - 可选；跟踪函数
     */
    scale(
        stageIndex: number,
        currentStages: ILayerProps[][],
        destStages: ILayerProps[][],
        currentSize: ISize,
        destSize: ISize,
        units: IAdjustUnit,
        pushHistory = true,
        trackFunc?: string,
    ) {
        this.framework.operate.delActiveLayer();
        const originIndex = this.framework.convertStageToOrigin(stageIndex);
        const useHeightScale = isFitHeight(currentSize, destSize);
        for (let i = 0; i < destStages.length; i++) {
            const layers = destStages[i];
            scaleLayers(
                this.framework.identity,
                layers,
                currentSize,
                destSize,
                useHeightScale,
                originIndex === this.framework.convertStageToOrigin(i),
                true,
            );
        }
        const newStages = cloneDeep(destStages);
        for (let i = 0; i < destStages.length; i++) {
            const layers = destStages[i];
            this.framework.stage.batchUpdate(layers, false, false);
        }
        this.framework.setRerender(originIndex);
        this.framework.rerender();
        // this.framework.updateExtraInfoSize(stageIndex);

        if (pushHistory) {
            this.framework.history.write({
                type: historyAdjustType,
                data: {
                    originIndex,
                    units,
                    oldStages: currentStages,
                    newStages,
                },
                trackFunc: trackFunc || trackConfig[LayerType.BACKGROUND_LAYER]?.resize,
            });
        }
    }

    /**
     * 替换图层
     *
     * @param ids - 旧图层 id 列表
     * @param newLayers - 新图层数据
     * @param historyFlag - 可选；历史记录标志
     * @param trackFunc - 可选；跟踪函数
     * @param autoActive - 可选；是否自动激活图层；默认值为 `true`
     * @param extraUpdateLayer - 可选；额外更新图层，不写进历史记录
     */
    replaceLayer(
        ids: string | string[],
        newLayers: ILayerProps | ILayerProps[],
        historyFlag?: unknown,
        trackFunc?: string,
        autoActive = true,
        extraUpdateLayer?: {
            oldLayers: ILayerProps[];
            newLayers: ILayerProps[];
        },
    ) {
        this.framework.operate.delActiveLayer();
        const res = this.framework.stage.replaceLayer(ids, newLayers);
        if (!res) return [];
        const { newIds, oldInfo: restoreData, activeId, wrapId } = res;
        this.framework.history.write({
            type: historyReplaceLayerType,
            data: {
                activeId,
                wrapId,
                newIds,
                restoreData,
                ids,
                newLayers,
                historyFlag,
                extraUpdateLayer,
            },
            trackFunc,
        });
        if (autoActive) {
            [activeId, wrapId].forEach((id) => {
                if (id && this.framework.getLayer(id)) {
                    this.framework.operate.setActiveLayer(id);
                }
            });
        }
        const layers = newIds.map((id) => this.framework.getLayer(id));
        this.framework.createFontFamilySubset(layers, true);
        return res.newIds;
    }

    /**
     * 静默替换图层，不记录历史，但确认时，需要通过 applyReplaceLayer 方法应用
     * @param ids
     * @param newLayers
     * @returns
     */
    muteReplaceLayer(ids: string | string[], newLayers: ILayerProps | ILayerProps[]) {
        this.framework.operate.delActiveLayer();
        const res = this.framework.stage.replaceLayer(ids, newLayers);
        if (!res) return null;
        const { newIds, activeId, wrapId } = res;

        [activeId, wrapId].forEach((id) => {
            if (id && this.framework.getLayer(id)) {
                this.framework.operate.setActiveLayer(id);
            }
        });

        const layers = newIds.map((id) => this.framework.getLayer(id));
        this.framework.createFontFamilySubset(layers, true);

        return res;
    }

    applyReplaceLayer(
        ids: string | string[],
        res: IReplaceLayerResult,
        newLayers: ILayerProps | ILayerProps[],
        historyFlag?: unknown,
        trackFunc?: string,
    ) {
        const { newIds, oldInfo: restoreData, activeId, wrapId } = res;

        this.framework.history.write({
            type: historyReplaceLayerType,
            data: {
                activeId,
                wrapId,
                newIds,
                restoreData,
                ids,
                newLayers,
                historyFlag,
            },
            trackFunc,
        });

        return res.newIds;
    }

    /** 交换舞台 */
    swapStages(stagesIndex: number[], destIndex: number, sourceIndex: number, activeIndex?: number) {
        const { oldIds, oldIndex, newIds, newIndex, oldStageSelection, newStageSelection } =
            this.framework.stage.swapStages(stagesIndex, destIndex, sourceIndex, activeIndex);
        this.framework.history.write({
            type: historyBaseType,
            data: {
                oldIds,
                oldIndex,
                newIds,
                newIndex,
            },
            skipCheckOriginIndex: true,
        });
        return {
            oldIds,
            oldIndex,
            newIds,
            newIndex,
            oldStageSelection,
            newStageSelection,
        };
    }

    /**
     * 推入一个舞台数据，没有记录历史，初始化使用
     *
     * @param stages - 舞台数据
     * @param extraInfos - 舞台额外信息
     * @param activeNewStage - 是否激活新舞台
     * @param waitFontInit - 是否等到第一个舞台字体初始化完成
     */
    async insertWithoutHistory(
        stages: ILayerProps[][],
        extraInfos: IExtraInfo[],
        activeNewStage = false,
        waitFontInit = false,
    ) {
        // console.log('====insertWithoutHistory', stages, extraInfos, activeNewStage);
        this.framework.addExtraInfos(extraInfos);
        const { addList } = this.framework.stage.addStages(stages, activeNewStage);
        // TODO 分享页的首页初字体始化时机问题，暂时先等首页字体加载完成后，在执行其他逻辑，后续需要问一下有没有其他办法处理可以内部触发更新。
        if (waitFontInit && addList[0] !== void 0) {
            await this.createStagesFontFamilySubset([addList[0]]);
            // 加载剩余的字体
            this.createStagesFontFamilySubset(addList.slice(1));
        } else {
            // 创建字体子集
            this.createStagesFontFamilySubset(addList);
        }
    }

    /**
     * 批量插入并写入历史记录
     *
     * @param stages - 舞台列表
     * @param extraInfos - 额外信息
     * @param activeNewStage - 可选；是否激活的新舞台；默认值为 `false`
     * @param scaleWidth - 可选；缩放宽度；默认值为 `false`
     * @param trackFunc - 可选；跟踪函数
     * @param insertIndex - 可选；插入索引序号
     */
    async batchInsert(
        stages: ILayerProps[][],
        extraInfos: IExtraInfo[],
        activeNewStage = false,
        scaleWidth = false,
        trackFunc?: string,
        insertIndex?: number, // 新增插入位置参数
    ) {
        this.framework.operate.delActiveLayer();
        let oldIds: number[] = [];
        let oldIndex: number = 0;
        let newIds: number[] = [];
        let newIndex: number = 0;
        const { originIndexs, insertId } = await this.framework.history.realEdit.insertStage(extraInfos.length);
        const insertData = insertId
            ? cloneDeep({
                  insertId,
                  originIndexs,
                  extraInfos: extraInfos,
                  stages: scaleWidth ? [] : stages,
              })
            : undefined;
        this.framework.addExtraInfos(extraInfos, originIndexs);
        const finalAddList: number[] = [];
        for (let i = 0; i < stages.length; i++) {
            const stage = stages[i];
            const originIndex = originIndexs[i];
            // 如果指定了插入位置，则依次在该位置后插入 否则使用默认位置
            const currentInsertIndex = insertIndex !== undefined ? insertIndex + i : undefined;
            const {
                addList,
                oldIndex: oldActiveStageIndex,
                oldIds: oldStageList,
                newIndex: newActiveStageIndex,
                newIds: newStageList,
            } = this.framework.stage.addStages(
                stage,
                i === 0 ? activeNewStage : false, // 需要激活第一个舞台
                currentInsertIndex,
                scaleWidth,
                [originIndex],
            );
            if (scaleWidth && insertData) {
                insertData.stages.push(this.framework.getLayers(originIndex));
            }
            finalAddList.push(...addList);
            if (i === 0) {
                oldIds = oldStageList;
                oldIndex = oldActiveStageIndex;
            }
            newIds = newStageList;
            newIndex = newActiveStageIndex;
        }
        const data: IStageHistoryBaseData = {
            oldIds,
            oldIndex,
            newIds,
            newIndex,
            insertData,
        };
        this.framework.history.write({
            type: historyBaseType,
            data,
            trackFunc: trackFunc || trackConfig.page.add,
            skipCheckOriginIndex: true,
        });
        // 创建字体子集
        this.createStagesFontFamilySubset(finalAddList);
    }

    /**
     * 插入到下一个
     *
     * @param layers - 舞台数据
     * @param extraInfo - 舞台额外信息
     * @param scaleWidth - 可选；是否缩放宽度；默认值为 `false`
     * @param trackFunc - 可选；跟踪函数
     */
    insertToNext(layers: ILayerProps[], extraInfo: IExtraInfo, scaleWidth = false, trackFunc?: string) {
        this.insertStage(layers, extraInfo, scaleWidth, this.framework.stage.getActiveStageIndex(), trackFunc);
    }

    /**
     * 插入到末尾
     *
     * @param layers - 舞台数据
     * @param extraInfo - 舞台额外信息
     * @param scaleWidth - 可选；是否缩放宽度；默认值为 `false`
     * @param trackFunc - 可选；跟踪函数
     */
    insertToLast(layers: ILayerProps[], extraInfo: IExtraInfo, scaleWidth = false, trackFunc?: string) {
        this.insertStage(layers, extraInfo, scaleWidth, undefined, trackFunc);
    }

    /**
     * 插入到指定位置
     *
     * @param layers - 舞台数据
     * @param extraInfo - 舞台额外信息
     * @param scaleWidth - 可选；是否缩放宽度；默认值为 `false`
     * @param index - 可选；插入位置
     * @param trackFunc - 可选；跟踪函数
     */
    insertToIndex(
        layers: ILayerProps[],
        extraInfo: IExtraInfo,
        scaleWidth = false,
        index?: number,
        trackFunc?: string,
    ) {
        this.insertStage(layers, extraInfo, scaleWidth, index, trackFunc);
    }

    /** 获取舞台数量 */
    count() {
        return this.framework.getRenderStageLength();
    }

    /** 获取所有舞台数据 */
    getStages() {
        return this.framework.getStages();
    }

    /** 设置舞台单位 */
    setUnit(unit: SizeUnitType) {
        this.framework.stage.setStageUnit(unit);
    }

    /** 获取舞台单位 */
    getUnit() {
        return this.framework.stage.getStageUnit();
    }

    /**
     * 监听舞台单位变化
     *
     * @param fn - 回调函数
     * @param weight - 可选；权重；；默认值为 `0`
     * @returns 监听器
     */
    onUnitChange(fn: (unit: SizeUnitType) => void, weight: number = 0) {
        return this.framework.events.onStageUnitChange(fn, weight);
    }

    /**
     * 监听舞台类型变化
     *
     * @param fn - 回调函数
     * @param weight - 可选；权重；；默认值为 `0`
     * @returns 监听器
     */
    onTypeChange(fn: (stageType: StageType) => void, weight: number = 0) {
        return this.framework.events.onStageTypeChange(fn, weight);
    }

    /** 设置舞台类型 */
    setType(stageType: StageType, canvasType: CanvasType) {
        this.framework.stage.setStageType(stageType, canvasType);
    }

    /** 获取舞台类型 */
    getType() {
        return this.framework.stage.getStageType();
    }

    getCanvasType() {
        return this.framework.stage.getCanvasType();
    }

    /** 获取视口宽度 */
    getViewWidth() {
        return this.framework.stage.getViewWidth();
    }

    /** 获取视口高度 */
    getViewHeight() {
        return this.framework.stage.getViewHeight();
    }

    /** 获取当前舞台尺寸 */
    getActiveSize(stageIndex?: number) {
        return this.framework.stage.getActiveStageSize(stageIndex);
    }

    /** 设置舞台是否可见 */
    setIsVisible(isVisible: boolean) {
        this.framework.stage.setIsStageVisible(isVisible);
    }

    /** 设置舞台图层是否隐藏 */
    setIsHideLayers(isHideLayers: boolean) {
        this.framework.stage.setIsHideLayers(isHideLayers);
    }

    /** 获取舞台是否可见 */
    getIsVisible() {
        return this.framework.stage.isStageVisible();
    }

    /**
     * 获取舞台尺寸
     * 单画布：当前激活的尺寸
     * 多画布：总长度
     */
    getSize(originIndex?: number) {
        if (isUndefined(originIndex)) {
            return this.framework.stage.getSize();
        }
        return this.framework.getSize(originIndex);
    }

    /** 获取舞台尺寸 */
    getSizeByIndex(stageIndex: number) {
        return this.framework.getSizeByIndex(stageIndex);
    }

    /** 获取选中的舞台索引 */
    getStageSelection() {
        return this.framework.stage.getStageSelection();
    }

    /** 设置选中的舞台索引 */
    setStageSelection(indexes: number[]) {
        this.framework.stage.setStageSelection(indexes);
        this.framework.events.emitStageSelectionChange(indexes);
    }

    /**
     * 监听选中的舞台索引变化
     * @param fn - 回调函数
     * @returns 解绑函数
     */
    onStageSelectionChange(fn: (indexes: number[]) => void) {
        const handler = this.framework.events.onStageSelectionChange(fn);

        return () => {
            this.framework.events.off(handler);
        };
    }

    /** stage index 转 stage origin index */
    convertStageToOrigin(index: number) {
        return this.framework.convertStageToOrigin(index);
    }

    /** stage origin index 转 stage index */
    convertOriginToStage(originIndex: number) {
        return this.framework.convertOriginToStage(originIndex);
    }

    /**
     * 监听舞台重新渲染
     * 舞台的【增/删/调换 位置】 都会触发
     * @param fn - 回调函数
     * @param weight - 可选；权重；默认值为 `0`
     * @returns 监听器
     */
    onRerenderAll(fn: (renderStageList: number[]) => void, weight: number = 0) {
        return this.framework.events.onRerenderAll(fn, weight);
    }

    /**
     * 监听舞台重新渲染
     * @param originIndex - 舞台原始索引
     * @param fn - 回调函数
     * @param weight - 可选；权重；默认值为 `0`
     * @returns 监听器
     */
    onRerenderStage(originIndex: number, fn: (originIndex: number) => void, weight = 0) {
        return this.framework.events.onRerenderStage(originIndex, fn, weight);
    }

    /**
     * 监听舞台尺寸变化
     *
     * @param fn - 回调函数
     * @param weight - 可选；权重；默认值为 `0`
     * @returns 监听器
     */
    onResize(fn: (args: IStageResizeData[]) => void, weight: number = 0) {
        return this.framework.events.onStageResize(fn, weight);
    }

    /** 获取舞台尺寸列表 */
    getSizeList() {
        return this.framework.getSizeList();
    }

    /**
     * 获取是否选中舞台
     *
     * @returns 是否选中舞台
     */
    isPick() {
        return this.framework.stage.isPickStage();
    }

    /** 设置是否选中激活舞台 */
    setIsPick(isPickStage: boolean) {
        this.framework.stage.setIsPickStage(isPickStage);
    }

    /**
     * 监听选中舞台
     *
     * @param fn - 回调函数
     * @param weight - 可选；权重；默认值为 `0`
     * @returns 监听器
     */
    onPickChange(fn: (isPickStage: boolean) => void, weight: number = 0) {
        return this.framework.events.onPickStageChange(fn, weight);
    }

    /** 获取舞台额外信息列表 */
    getExtraInfoList() {
        return this.framework.getExtraInfoList();
    }

    /** 获取舞台额外信息 */
    getExtraInfo(stageIndex: number) {
        return this.framework.getExtraInfo(stageIndex);
    }

    /** 设置舞台额外信息 */
    setExtraInfo(stageIndex: number, extraInfo: IExtraInfo) {
        this.framework.setExtraInfo(stageIndex, extraInfo);
    }

    /**
     * 获取渲染图层 ids
     *
     * @param originIndex - 舞台索引
     */
    getRenderIds(originIndex: number) {
        return this.framework.getRenderIds(originIndex);
    }

    /** 获取渲染舞台列表 */
    getRenderStageList() {
        return this.framework.getRenderStage();
    }

    /** 获取原始舞台列表 */
    getOriginIdsList() {
        return this.framework.getOriginIdsList();
    }

    /**
     * 监听图层变化, 默认全部图层变化
     *
     * @param fn - 回调函数
     * @param layerId - 可选；图层 ID
     */
    onLayerChange(fn: () => void, layerId?: string) {
        return this.framework.events.onLayerChange(fn, layerId);
    }

    /**
     * 监听图层双击
     *
     * @param fn - 回调函数
     */
    onLayerDoubleClick(fn: (layerId: string, point: IPoint) => void) {
        return this.framework.events.onLayerDoubleClick(fn);
    }

    /** 获取指定画布的所有图层 */
    getLayers(originIndex: number) {
        return this.framework.getLayers(originIndex);
    }

    /** 获取指定舞台的所有图层 */
    getLayersByIndex(stageIndex: number) {
        return this.framework.getLayersByIndex(stageIndex);
    }

    /** 更新图层 */
    updateLayer(layer: ILayerProps) {
        this.addLayer(layer);
    }

    /**
     * 获取指定图层
     *
     * @param id - 图层 ID
     * @param withClone - 可选；是否克隆；默认值为 `false`
     * @returns 图层
     */
    getLayer(id: string, withClone: boolean = false) {
        return this.framework.getLayer(id, withClone);
    }

    /** 添加图层, 不写入历史, 会创建字体子集 */
    addLayer(layer: ILayerProps, rerenderOriginIndex?: number) {
        const res = this.framework.stage.addOrUpdate(layer, undefined, rerenderOriginIndex);
        this.framework.createFontFamilySubset([res], true);
    }

    /** 滚动到指定舞台 */
    scrollTo(index: number) {
        this.framework.operate.stageScroll.scrollTo(index);
    }

    /** 滚动到激活舞台 */
    scrollToActiveStage() {
        this.framework.operate.stageScroll.scrollToActiveStage();
    }

    /** 设置激活舞台索引 */
    setActiveStageIndex(stageIndex: number) {
        this.framework.stage.setActiveStageIndex(stageIndex);
    }

    /** 获取舞台数据 */
    getStageByIndex(stageIndex: number) {
        const originIndex = this.convertStageToOrigin(stageIndex);
        return this.framework.getLayers(originIndex);
    }

    /** 转换图层数据为激活数据 */
    getOriginLayerInfo(layer: ILayerProps) {
        return exportLayerOriginInfo(this.framework.identity, layer);
    }

    /** 缩放图层 */
    scaleLayer(
        layer: ILayerProps,
        offsetWidth: number,
        direction = DirectionType.POSITIVE,
        disableWithGroupHasMinLayer = true,
        callback?: (layer: ILayerProps) => void,
    ) {
        scaleLayer(
            this.framework.identity,
            layer,
            offsetWidth,
            direction,
            disableWithGroupHasMinLayer,
            false,
            callback,
        );
    }

    /**
     * 获取外壳图层
     *
     * @param id - 图层 ID
     * @param extraGroupIsWrap - 可选；临时组是否可以作为外壳；默认值为 `false`
     */
    getWrapLayer(layerId: string, extraGroupIsWrap = false) {
        return this.framework.getWrapLayer(layerId, extraGroupIsWrap);
    }

    /**
     * 批量更新图层
     *
     * @param layers - 图层数据
     * @param skipChild - 可选；是否跳过子图层
     */
    batchUpdateLayer(layers: ILayerProps[], skipChild?: boolean, autoRerender?: boolean) {
        return this.framework.stage.batchUpdate(layers, skipChild, autoRerender);
    }

    /** 隐藏素材层 */
    hideMaterial(disableHideFn?: (layer: ILayerProps) => boolean, originIndex?: number) {
        return this.framework.stage.hideMaterial(disableHideFn, originIndex);
    }

    /** 显示素材层 */
    showMaterial(disableHideFn?: (layer: ILayerProps) => boolean, originIndex?: number) {
        return this.framework.stage.showMaterial(disableHideFn, originIndex);
    }

    /** 获取悬停图层 ID */
    getHoverId() {
        return this.framework.operate.getHoverId();
    }

    /** 获取是否正在执行鼠标框选 */
    getIsMouseSelect() {
        return this.framework.operate.isMouseSelect();
    }
}
